/**
 * Detection of the native container (the Farmbot mobile app), from the
 * `FarmbotApp/<version>` token it appends to the WebView User-Agent.
 *
 * Read from the UA rather than an injected flag: `injectedJavaScriptBeforeContentLoaded`
 * is documented-unreliable on Android and can run after page scripts, so a flag
 * may be absent on first render. The UA is set natively and present on the
 * first request.
 */

/**
 * One pattern for both detection and version parsing, so the two cannot
 * disagree about what counts as the container.
 *
 * A bare `includes('FarmbotApp/')` also matched `NotFarmbotApp/1.0.0` and a
 * versionless `FarmbotApp/`, neither of which the container ever sends. The
 * leading boundary and the required semver keep detection to exactly what
 * `nativeBridge.ts` in the app produces.
 *
 * The boundary is any non-letter rather than whitespace specifically. Both
 * platforms do separate the token with a space today — Android formats
 * `"$defaultUserAgent $applicationName"` and iOS `@"%@ %@"` — but that is
 * react-native-webview's business, not a contract, and detection failing
 * closed would make the app render as a plain browser. A non-letter boundary
 * still rejects `NotFarmbotApp/`, which is the point of having one.
 */
const NATIVE_CONTAINER_UA_PATTERN = /(?:^|[^A-Za-z])FarmbotApp\/(\d+\.\d+\.\d+)/;

/** True when the platform is running inside the Farmbot mobile app. */
export function isNativeContainer(): boolean {
  if (typeof navigator === 'undefined') return false;
  return NATIVE_CONTAINER_UA_PATTERN.test(navigator.userAgent);
}

/**
 * The container's app version, or null outside it. The platform deploys
 * continuously while container updates go through app review, so a behaviour
 * needing a newer container can degrade on this.
 */
export function getNativeContainerVersion(): string | null {
  if (typeof navigator === 'undefined') return null;
  const match = navigator.userAgent.match(NATIVE_CONTAINER_UA_PATTERN);
  return match ? match[1] : null;
}
